import { ChangeEvent, FC, ReactNode } from 'react';
import MuiSwitch from '@mui/material/Switch';
import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';
import classNames from 'classnames';
import { FormInputSize } from '../form/helpers';

interface Props {
  checked: boolean;
  onChange: (event: ChangeEvent) => void;
  disabled?: boolean;
  className?: string;
  checkedIcon?: ReactNode;
  icon?: ReactNode;
  size?: FormInputSize;
}

const useStyles = createUseStyles((theme: Theme) => ({
  switch: {
    width: 45,
    height: 25,
    padding: 0,
    '&.small': {
      width: 36,
      height: 20,

      '& .MuiSwitch-switchBase': {
        margin: 3,
        '&.Mui-checked': {
          transform: 'translateX(16px)',
        },
      },
      '& .MuiSwitch-thumb': {
        width: 14,
        height: 14,
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 4,
      transitionDuration: '100ms',
      '&:hover, &:active': {
        backgroundColor: 'transparent',
      },
      '&.Mui-checked': {
        transform: 'translateX(20px)',
        color: '#fff',
        '&:hover, &:active': {
          backgroundColor: 'transparent',
        },
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.toggle.on,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.12)',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.subtleGray,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 17,
      height: 17,
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor: theme.palette.toggle.off,
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 250,
      }),
    },
    '&:hover .MuiSwitch-track': {
      backgroundColor: theme.palette.toggle.offHover,
    },
    '&:hover .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.toggle.onHover,
    },
  },
}));

const BaseSwitch: FC<Props> = ({ className, checked, disabled, onChange, size, ...rest }) => {
  const styles = useStyles();

  return (
    <MuiSwitch
      checked={checked}
      disabled={disabled}
      inputProps={{ 'aria-label': 'controlled' }}
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      className={classNames(styles.switch, className, size)}
      onChange={onChange}
      {...rest}
    />
  );
};

export default BaseSwitch;
